import {useEffect, useState, useMemo} from 'react';
import { useRouter } from '@backpackjs/storefront';
import {DateTime} from 'luxon';
import Link from 'next/link';

import { useSettings } from '@backpackjs/storefront';

import {useCountdown} from '@hooks';

function DateTimeDisplay({value, type, isDanger, className = '', timerLabelInline = true}) {
  return (
    <div
      className={`${
        isDanger ? 'countdown danger' : 'countdown'
      } flex items-center font-hafferBold ${className}`}
    >
      {value && <span>{value < 10 && value > 0 ? `0${value}` : Math.max(0, value)}</span>}
      <span className={`capitalize leading-none font-primary font-medium ${timerLabelInline ? 'max-md:hidden' : ''}`}>{type}</span>
      {timerLabelInline ? (
        <span className="capitalize leading-none font-primary font-medium md:hidden">{type?.substring(0,1)}</span>
      ) : null}
    </div>
  );
}

function ExpiredNotice() {
  return null;
}

function ShowCountDown(props) {
  const {days, hours, minutes, seconds, backgroundColor, clockColor, timerLabelInline = true, className = ''} = props;
  const [second, setSecond] = useState(null);
  useEffect(() => {
    setSecond(seconds);
  }, [seconds]);
  return (
    <div
      className={`coundown-timer flex items-center gap-1.5 md:gap-2 font-bold font-primary text-xsmall md:text-small uppercase ${className}`}
      style={{color: clockColor || '#000000'}}
    >
      <DateTimeDisplay
        value={days}
        type="Days"
        isDanger={days <= 3}
        className={timerLabelInline ? 'gap-1' : 'flex-col'}
        timerLabelInline={timerLabelInline}
      />
      <div className="flex items-center">:</div>
      <DateTimeDisplay
        value={hours}
        type="Hrs"
        isDanger={false}
        className={timerLabelInline ? 'gap-1' : 'flex-col'}
        timerLabelInline={timerLabelInline}
      />
      <div className="flex items-center">:</div>
      <DateTimeDisplay
        value={minutes}
        type="Min"
        isDanger={false}
        className={timerLabelInline ? 'gap-1' : 'flex-col'}
        timerLabelInline={timerLabelInline}
      />
      <div className="flex items-center">:</div>
      <DateTimeDisplay
        value={second}
        type="Sec"
        isDanger={false}
        className={timerLabelInline ? 'gap-1' : 'flex-col'}
        timerLabelInline={timerLabelInline}
      />
    </div>
  );
}

function CountdownTimer({countdown}) {
  const settings = useSettings();
  const {pathname} = useRouter();
  const {enable = 'global', enableExpired = true, layout, backgroundColor, clockColor, timerLabelInline = true, targetTime, timezone, title} = settings?.countdown || {};
  const cta = settings?.countdown?.cta;
  const targetDate = targetTime;
  const targetDateZone = DateTime.fromFormat(
    `${targetDate} ${timezone || 'America/Los_Angeles'}`,
    'yyyy-MM-dd TT z',
  );
  const [days, hours, minutes, seconds] = useCountdown(targetDateZone.toJSDate());
  const buttonEnable = cta?.link?.url && cta?.link?.text;

  const ctaPosition = cta?.ctaPosition;

  const layoutClass = useMemo(() => {
    let className = '';
    if (buttonEnable) {
      if (layout === 'center') {
        className += 'grid max-md:grid-rows-none max-md:grid-cols-[1fr_min-content] md:flex';
      } else {
        className += 'grid max-md:grid-rows-none grid-cols-[1fr_min-content] md:grid-cols-[1fr_max-content_1fr]';
        if (ctaPosition !== 'right') {
          className += ' md:flex';
        }
      }
    }
    if (layout === 'center') {
      className += ' flex flex-col md:flex-row justify-center';
    } else {
      className += ' flex justify-between';
    }
    return className;
  }, [layout, buttonEnable, ctaPosition]);

  const countDownClass = useMemo(() => {
    if (layout !== 'center') {
      if (!buttonEnable) {
        return 'md:ml-auto';
      }
      return ctaPosition === 'right' ? 'md:mx-auto' : 'md:ml-auto';
    }
    return '';
  }, [ctaPosition, layout, buttonEnable]);

  const countdownEnable = useMemo(() => {
    if (enable === 'global') return true;
    if (enable === 'homepage' && pathname === '/') {
       return true;
    }
    return false
  }, [enable, pathname])

  if (!countdownEnable || !targetDateZone.isValid) return null;

  if ((days + hours + minutes + seconds <= 0) && !enableExpired) {
    return <ExpiredNotice />;
  }

  return (
    <div className={`countdown-timer items-center gap-x-6 gap-y-0 w-full px-4 md:px-12 py-2 ${layoutClass}`}
      style={{backgroundColor: backgroundColor || '#EDF0F5', color: clockColor || '#000000'}}
    >
      {title ? (
        <div
          className="countdown__title mb-0 font-primary text-xsmall md:text-small max-md:row-start-1 [&>:first-child]:mt-0 [&>:last-child]:mb-0"
          dangerouslySetInnerHTML={{__html: title}}
        />
      ) : null}

      {ctaPosition !== 'right' && buttonEnable ? (
        <div className={`max-md:row-start-1 max-md:row-end-3 max-md:col-start-2 countdown__cta flex items-center ${layout !== 'center' && ctaPosition === 'right' ? 'md:ml-auto' : ''}`}>
          <Link href={cta.link.url}>
            <a className={`${cta?.style || 'focus-link'} ${ cta?.style !== 'focus-link' ? '!py-2 !min-w-0' : ''} text-xsmall md:text-small font-normal whitespace-nowrap no-underline`}>
              {cta.link.text}
            </a>
          </Link>
        </div>
      ) : null}

      <ShowCountDown
        days={days}
        hours={hours}
        minutes={minutes}
        seconds={seconds}
        clockColor={clockColor}
        timerLabelInline={timerLabelInline}
        className={`max-md:row-start-2 ${countDownClass}`}
      />
      {ctaPosition === 'right' && buttonEnable ? (
        <div className={`max-md:row-start-1 max-md:row-end-3 max-md:col-start-2 countdown__cta flex items-center ${layout !== 'center' && ctaPosition === 'right' ? 'md:ml-auto' : ''}`}>
          <Link href={cta.link.url}>
            <a className={`${cta?.style || 'focus-link'} ${ cta?.style !== 'focus-link' ? '!py-2 !min-w-0' : ''} text-xsmall md:text-small font-normal whitespace-nowrap no-underline`}>
              {cta.link.text}
            </a>
          </Link>
        </div>
      ) : null}
    </div>
  );
}

export default CountdownTimer;